import React, { createContext, useContext, useEffect, useState } from "react";

/**
 * Bilingual (EN / DE) language layer for the JUMIBI site.
 * Mirrors the behaviour of the static site's js/site.js:
 *   - default = saved choice, else browser language (de if it starts with "de")
 *   - persisted in localStorage under "jumibi-lang"
 *   - reflected on <html lang> and <html data-lang> for completeness
 * Unlike the static site (which renders both languages and hides one via CSS),
 * here we keep a single source of truth in React state and render only the
 * active language via the <Bi> helper.
 */
const LangContext = createContext({ lang: "de", setLang: () => {} });

export function LangProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try {
      const saved = localStorage.getItem("jumibi-lang");
      if (saved === "de" || saved === "en") return saved;
    } catch (e) {}
    const browser = (navigator.language || "en").toLowerCase();
    return browser.startsWith("de") ? "de" : "en";
  });

  const setLang = (next) => {
    setLangState(next);
    try {
      localStorage.setItem("jumibi-lang", next);
    } catch (e) {}
  };

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.setAttribute("data-lang", lang);
  }, [lang]);

  return (
    <LangContext.Provider value={{ lang, setLang }}>
      {children}
    </LangContext.Provider>
  );
}

export function useLang() {
  return useContext(LangContext);
}

/**
 * Render the active-language variant. Children may be strings or JSX.
 * <Bi en="Book a demo" de="Demo buchen" />
 */
export function Bi({ en, de }) {
  const { lang } = useLang();
  return <>{lang === "de" ? de : en}</>;
}

/** Imperative variant for attributes / plain strings. */
export function useT() {
  const { lang } = useLang();
  return (en, de) => (lang === "de" ? de : en);
}
